import { NamespacedStoreState } from '@Types';
import { moduleNamespace } from '@Store/config.store';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

const parseNamespace = (state: NamespacedStoreState) => state[moduleNamespace];

const failureGradeStoreSelector = createSelector(parseNamespace, state => state.FailureGradeStoreState);
export const assetBreakDownChartStoreSelector = createSelector(
	parseNamespace,
	state => state.AssetBreakDownChartStoreState,
);

export const selectAssetBreakDownChartStore = () => useSelector(assetBreakDownChartStoreSelector);

export const failureGradeMatrixResultSelector = createSelector(
	failureGradeStoreSelector,
	failureGradeStoreSelector => ({
		failure_probabilities: failureGradeStoreSelector?.failureGradeMatrixResult?.failure_probabilities || [],
		ruls: failureGradeStoreSelector?.failureGradeMatrixResult?.ruls || [],
		matrix: failureGradeStoreSelector?.failureGradeMatrixResult?.matrix || [],
		latestRunResultId: failureGradeStoreSelector?.latestRunResultId || null,
	}),
);

export const selectFailureGradeMatrixResults = () => useSelector(failureGradeMatrixResultSelector);

export const failureGradeMatrixResultIsLoadingSelector = createSelector(
	failureGradeStoreSelector,
	failureGradeStoreSelector => failureGradeStoreSelector?.inProcess,
);

export const selectFailureGradeMatrixIsLoading = () => useSelector(failureGradeMatrixResultIsLoadingSelector);

export const failureGradeMatrixResultIsSuccessfulSelector = createSelector(
	failureGradeStoreSelector,
	failureGradeStoreSelector => failureGradeStoreSelector?.successful,
);

export const selectFailureGradeMatrixIsSuccessful = () => useSelector(failureGradeMatrixResultIsSuccessfulSelector);

// Selector to get aggregated grade counts from the matrix
export const failureGradeAggregatedCountsSelector = createSelector(
	failureGradeStoreSelector,
	failureGradeStoreSelector => {
		const matrix = failureGradeStoreSelector?.failureGradeMatrixResult?.matrix;

		if (!matrix || matrix.length === 0) {
			return {
				extreme: 0,
				high: 0,
				medium: 0,
				low: 0,
				negligible: 0,
				total: 0,
			};
		}

		// Flatten the matrix and aggregate counts by grade
		const aggregatedCounts = matrix.flat().reduce(
			(acc, cell) => {
				const grade = cell.grade;
				const count = cell.count;

				if (grade === 'extreme') {
					acc.extreme += count;
				} else if (grade === 'high') {
					acc.high += count;
				} else if (grade === 'medium') {
					acc.medium += count;
				} else if (grade === 'low') {
					acc.low += count;
				} else {
					// Handle any other grades as negligible
					acc.negligible += count;
				}

				acc.total += count;
				return acc;
			},
			{ extreme: 0, high: 0, medium: 0, low: 0, negligible: 0, total: 0 },
		);

		return aggregatedCounts;
	},
);

export const selectFailureGradeAggregatedCounts = () => useSelector(failureGradeAggregatedCountsSelector);
